import { useEffect, useMemo, useRef, useState } from 'react'
import { Vehicle, VehicleColor } from '@otw/models/inventory'
import { useFormulasWithRules } from 'hooks/graybook/useFormulasWithRules'
import { matchFormula } from 'hooks/graybook/matchFormula'
import { useOLSPrediction } from '@/hooks/ols/useOLSPrediction'
import { resolveGrayBookPrice } from '@/views/SuperAdmin/Tools/GrayBook/LTVCalculator/util/resolveGrayBookPrice'

export function useVehicleGrayBookPrices(vehicles: Vehicle[], enabled: boolean) {
  const { formulas: formulasWithRules } = useFormulasWithRules({})
  const { predictPricesBatch } = useOLSPrediction()
  const [pricesByVehicleId, setPricesByVehicleId] = useState<Record<string, number | null>>({})
  const [isLoading, setIsLoading] = useState(false)
  const lastResolvedKey = useRef('')

  const pricingKey = useMemo(
    () =>
      enabled
        ? vehicles
            .map(v =>
              `${v.id}:${v.year}:${v.miles}:${v.color}:${v.make}:${v.model}:${v.type}:${v.size}:${v.fuel}`,
            )
            .join('|')
        : '',
    [enabled, vehicles],
  )

  useEffect(() => {
    if (!enabled || !pricingKey || !formulasWithRules?.length) {
      if (!enabled) {
        setPricesByVehicleId({})
        setIsLoading(false)
      }
      return
    }

    if (pricingKey === lastResolvedKey.current) return

    let stale = false
    lastResolvedKey.current = pricingKey
    setIsLoading(true)

    const resolveFallback = (vehicle: Vehicle): number | null => {
      const formula = matchFormula(formulasWithRules, vehicle)
      if (!formula || !vehicle.year || !vehicle.miles) return null

      const resolved = resolveGrayBookPrice({
        year: vehicle.year,
        mileage: vehicle.miles,
        color: (vehicle.color as VehicleColor) || 'White',
        formula,
        formulasWithRules,
        olsVersion: null,
      })

      return resolved.price
    }

    const olsEligible = vehicles.filter(v => matchFormula(formulasWithRules, v) && v.year && v.miles != null)

    const finish = (next: Record<string, number | null>) => {
      if (stale) return
      setPricesByVehicleId(next)
      setIsLoading(false)
    }

    if (olsEligible.length === 0) {
      finish(
        vehicles.reduce<Record<string, number | null>>((acc, vehicle) => {
          acc[vehicle.id] = resolveFallback(vehicle)
          return acc
        }, {}),
      )
      return
    }

    predictPricesBatch(
      olsEligible.map(vehicle => {
        const formula = matchFormula(formulasWithRules, vehicle)!
        return {
          make: formula.vehicleMake,
          model: formula.vehicleModel,
          type: formula.vehicleType,
          size: formula.vehicleSize,
          fuel: formula.vehicleFuel as 'Gas' | 'Diesel',
          year: vehicle.year!,
          mileage: vehicle.miles!,
          color: (vehicle.color as VehicleColor) || 'White',
        }
      }),
    )
      .then(results => {
        if (stale) return

        const next = vehicles.reduce<Record<string, number | null>>((acc, vehicle) => {
          const idx = olsEligible.findIndex(v => v.id === vehicle.id)
          if (idx === -1) {
            acc[vehicle.id] = resolveFallback(vehicle)
            return acc
          }

          const result = results[idx]
          if (result?.sellPrice != null && !result.usedFallback) {
            acc[vehicle.id] = result.sellPrice
          } else {
            acc[vehicle.id] = resolveFallback(vehicle)
          }
          return acc
        }, {})

        finish(next)
      })
      .catch(() => {
        if (stale) return
        finish(
          vehicles.reduce<Record<string, number | null>>((acc, vehicle) => {
            acc[vehicle.id] = resolveFallback(vehicle)
            return acc
          }, {}),
        )
      })

    return () => {
      stale = true
    }
  }, [enabled, pricingKey, formulasWithRules, predictPricesBatch, vehicles])

  return { grayBookPricesByVehicleId: pricesByVehicleId, isLoadingGrayBookPrices: isLoading }
}
